import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { formatDateOnly, formatDisplayDate, parseCrawlDate } from './commons';
import Diff, { CONTENT_KEYS } from './Diff';

export default function Changes() {
    const { curSnapshotDate, pageChanges, chartFilters } = useSelector(s => s.currentMonitoredUri);
    const activeTypes = chartFilters.changeTypes;

    const changeRecord = useMemo(() =>
        (pageChanges || []).find(pc => pc.crawlDate?.startsWith(curSnapshotDate)),
        [pageChanges, curSnapshotDate]
    );

    // Build before/after values from the changelog running state.
    // Do NOT use pageSnapshotsMap or /archive-api/page — the changelog carries all the
    // data we need: afterValues from c.newValue, beforeValues from the running state of
    // prior entries. No archive call, no dependency on snapshot loading state.
    const { beforeValues, afterValues, prevLabel } = useMemo(() => {
        if (!changeRecord || !Array.isArray(pageChanges)) return {};

        const sorted = [...pageChanges]
            .filter(pc => pc.changes?.length > 0)
            .sort((a, b) => new Date(a.crawlDate) - new Date(b.crawlDate));

        const runningState = {};
        let lastDate = null;

        for (const pc of sorted) {
            const dateStr = formatDateOnly(parseCrawlDate(pc.crawlDate));
            if (dateStr === curSnapshotDate) {
                const before = {};
                const after  = {};
                pc.changes.forEach(c => {
                    before[c.key] = runningState[c.key] ?? null;
                    after[c.key]  = c.newValue;
                });
                return { beforeValues: before, afterValues: after, prevLabel: lastDate || 'No prior data' };
            }
            pc.changes.forEach(c => { runningState[c.key] = c.newValue; });
            lastDate = dateStr;
        }

        return {};
    }, [changeRecord, pageChanges, curSnapshotDate]);

    const entryCrawlStatus = useMemo(() => {
        if (!changeRecord) return null;
        const statusChange = changeRecord.changes.find(c => c.key === 'crawlStatus');
        if (statusChange?.newValue) return statusChange.newValue;
        const redirectChange = changeRecord.changes.find(c => c.key === 'redirectChainElements');
        if (Array.isArray(redirectChange?.newValue) && redirectChange.newValue.length === 0) return 'crawl-failed';
        return null;
    }, [changeRecord]);

    const filteredChangeRecord = useMemo(() => {
        if (!changeRecord || activeTypes.length === 0) return changeRecord;
        return changeRecord.changes.some(c => activeTypes.includes(c.key)) ? changeRecord : null;
    }, [changeRecord, activeTypes]);

    // When there's an error (non-200), always show crawlStatus and suppress content fields
    // since meta/title changes during errors are noise, not signal.
    const isError = !!entryCrawlStatus;
    const changedKeys = useMemo(() => {
        if (!filteredChangeRecord) return [];
        if (activeTypes.length > 0) return activeTypes;
        let keys = filteredChangeRecord.changes.map(c => c.key);
        if (isError) {
            keys = keys.filter(k => !CONTENT_KEYS.has(k));
            if (!keys.includes('crawlStatus')) keys = ['crawlStatus', ...keys];
        }
        return keys;
    }, [activeTypes, filteredChangeRecord, isError]);

    const effectiveAfterValues = useMemo(() => {
        if (isError && entryCrawlStatus && !afterValues?.crawlStatus) {
            return { ...afterValues, crawlStatus: entryCrawlStatus };
        }
        return afterValues;
    }, [afterValues, isError, entryCrawlStatus]);

    if (!filteredChangeRecord && changeRecord) return (
        <Empty>
            <EmptyTitle>No changes match the active filter.</EmptyTitle>
        </Empty>
    );

    if (!filteredChangeRecord) return (
        <NoChangeBanner>No changes detected — this crawl was identical to the previous one.</NoChangeBanner>
    );

    return (
        <Wrap>
            <Diff
                changedKeys={changedKeys}
                beforeValues={beforeValues}
                afterValues={effectiveAfterValues}
                entryCrawlStatus={entryCrawlStatus}
                prevLabel={prevLabel === 'No prior data' ? 'No prior data' : formatDisplayDate(prevLabel)}
                currLabel={formatDisplayDate(curSnapshotDate)}
            />
        </Wrap>
    );
}

const Wrap = styled.div`
    overflow-x: auto;
    font-family: "Inter", system-ui, sans-serif;
`;

const Empty = styled.div`
    padding: 40px 24px;
    text-align: center;
    font-family: "Inter", system-ui, sans-serif;
`;

const EmptyTitle = styled.div`
    font-size: 0.85rem;
    font-weight: 600;
    color: #64748b;
`;

const NoChangeBanner = styled.div`
    padding: 10px 16px;
    background: #eff6ff;
    border-bottom: 1px solid #bfdbfe;
    font-size: 0.75rem;
    font-weight: 600;
    color: #1d4ed8;
    font-family: "Inter", system-ui, sans-serif;
`;
